<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/apiClient";
import CustomerSearch from "@/components/CustomerSearch.vue";
import ContactSearch from "@/components/ContactSearch.vue";

const route = useRoute();
const router = useRouter();
const types = ref([]);
const selectedType = ref(null);
const creationMode = ref("email");
const customer = ref(null);
const recipientContact = ref(null);
const description = ref("");
const notes = ref("");
const manualEmailSubject = ref("");
const manualEmailText = ref("");
const manualEmailAttachments = ref([]);
const extractLoading = ref(false);
const extractError = ref(null);
const extractProgress = ref(0);
const extractStage = ref("");
const extractedDraft = ref(null);
const extractedQuestions = ref([]);
const extractedMatchedCustomer = ref(null);
const extractedMatchedRecipient = ref(null);
const extractedDraftApplied = ref(false);
const extractionQuestionsAcknowledged = ref(false);
const preExtractionValues = ref(null);
const suggestedCustomer = ref(null);
const suggestedRecipient = ref(null);
const activeExtractionId = ref(null);
const attachmentUploadError = ref(null);
const createdQuoteId = ref(null);
const createdQuotePayload = ref(null);
const error = ref(null);
const loading = ref(false);
const createProgress = ref(0);
const createStage = ref("");
let createProgressTimer = null;
let extractProgressTimer = null;
let extractPollTimer = null;
let extractionGeneration = 0;
const createProgressIntervalMs = 400;
const createProgressFastStep = 3;
const createProgressSlowStep = 1;
const extractProgressIntervalMs = 350;
const extractProgressFastStep = 4;
const extractProgressSlowStep = 1;
const extractPollIntervalMs = 2000;
const ticketId = computed(() => {
  const raw = route.query.ticket_id;
  if (!raw) return null;
  const id = Number(raw);
  return Number.isNaN(id) ? null : id;
});
const customerId = computed(() => {
  const raw = route.query.customer_id ?? route.query.customerId;
  if (!raw) return null;
  const id = Number(raw);
  return Number.isNaN(id) ? null : id;
});
const selectedTypeRecord = computed(
  () =>
    types.value.find((type) => Number(type.id) === Number(selectedType.value)) ??
    null,
);
const manualExtractionEnabled = computed(
  () =>
    creationMode.value === "email" &&
    (isEventType(selectedTypeRecord.value) || isPtsType(selectedTypeRecord.value)),
);
const isPtsQuote = computed(() => isPtsType(selectedTypeRecord.value));
const extractedPtsRisk = computed(() => extractedVersion.value?.pts_risk ?? {});
const extractedVersion = computed(
  () => extractedDraft.value?.versions?.[0] ?? null,
);
const extractedDateCount = computed(
  () => extractedVersion.value?.dates?.length ?? 0,
);
const extractedResourceCount = computed(() =>
  (extractedVersion.value?.dates ?? []).reduce(
    (total, entry) => total + (Array.isArray(entry?.resources) ? entry.resources.length : 0),
    0,
  ),
);
const extractedAttachmentCount = computed(() => manualEmailAttachments.value.length);
const extractedEventRisk = computed(
  () => extractedVersion.value?.event_risk ?? {},
);
const recipientContactLabel = computed(() => {
  if (!recipientContact.value) return "";
  if (recipientContact.value.name && recipientContact.value.email) {
    return `${recipientContact.value.name} <${recipientContact.value.email}>`;
  }

  return recipientContact.value.name ?? recipientContact.value.email ?? "";
});

onMounted(async () => {
  const response = await apiClient.get("/api/reference/types");
  types.value = response.data;

  if (!selectedType.value && types.value.length) {
    const preferredType = (!ticketId.value
      ? types.value.find((type) => isEventType(type))
      : null) ?? types.value[0];
    selectedType.value = preferredType?.id ?? null;
  }

  await preloadTicketRecipient();
});

onBeforeUnmount(() => {
  extractionGeneration += 1;
  stopCreateProgressTimer();
  stopExtractProgressTimer();
  stopExtractPolling();
});

const normalizeTicketItems = (ticket, newestFirst = true) => {
  const items = Array.isArray(ticket?.items) ? ticket.items : [];

  return items.slice().sort((a, b) => {
    const aId = Number(a?.id ?? 0);
    const bId = Number(b?.id ?? 0);
    return newestFirst ? bId - aId : aId - bId;
  });
};

const cleanTicketSubject = (value) =>
  String(value ?? "")
    .replace(/\s*\[Ticket\s*#[^\]]+\]/gi, "")
    .replace(/^\s*(re|fw|fwd)\s*:\s*/i, "")
    .trim();

const cleanCandidateName = (value) => {
  let clean = String(value ?? "").trim();
  clean = clean.replace(/\s+/g, " ");
  clean = clean.replace(/^[\s"'.,;:-]+|[\s"'.,;:-]+$/g, "");
  clean = clean
    .replace(/\b(?:on|at|in)\b\s+\d{1,2}[/-]\d{1,2}(?:[/-]\d{2,4})?.*$/i, "")
    .trim();
  if (!clean || clean.length < 3) return null;
  return clean.slice(0, 120).trim();
};

const stripLeadInPhrases = (value) =>
  String(value ?? "")
    .replace(/^\s*(can you|could you|please)\s+/i, "")
    .replace(/^\s*(quote|quotation)\s+(for|re)\s+/i, "")
    .replace(/^\s*(event|medical)\s+cover\s+(for|re)\s+/i, "")
    .replace(/^\s*(request|enquiry)\s*(for|re)?\s*/i, "")
    .trim();

const extractNameFromText = (value, allowLooseFallback = false) => {
  const normalized = String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return null;

  const patterns = [
    /\bevent(?:\s+name)?\s*(?:is|:)\s*["']?([^"'.\n\r,;]{3,90})/i,
    /\b(?:called|named)\s*["']?([^"'.\n\r,;]{3,90})/i,
    /\bfor\s+["']?([^"'.\n\r,;]{3,90})\s+(?:event|festival|concert|show|match|race|party)\b/i,
    /"([^"]{3,90})"/,
  ];

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (match?.[1]) {
      const cleaned = cleanCandidateName(match[1]);
      if (cleaned) return cleaned;
    }
  }

  if (!allowLooseFallback) return null;
  return cleanCandidateName(stripLeadInPhrases(normalized));
};

const resolveTicketName = (ticket) => {
  const sortedItems = normalizeTicketItems(ticket, false);
  const externalItems = sortedItems.filter((item) => item?.contact?.id);
  for (const item of externalItems) {
    if (item?.text) {
      const extracted = extractNameFromText(item.text, false);
      if (extracted) return extracted;
    }
  }

  if (!externalItems.length) {
    for (const item of sortedItems) {
      if (item?.text) {
        const extracted = extractNameFromText(item.text, false);
        if (extracted) return extracted;
      }
    }
  }

  const subject = cleanTicketSubject(ticket?.subject);
  if (subject) {
    const extracted = extractNameFromText(subject, true);
    if (extracted) return extracted;
  }

  return null;
};

const resolveTicketContact = (ticket) => {
  const items = normalizeTicketItems(ticket);
  const externalItem = items.find((item) => item?.contact?.id);
  if (externalItem?.contact?.id) {
    return externalItem.contact;
  }

  const recipientItem = items.find((item) => item?.recipient?.id);
  if (recipientItem?.recipient?.id) {
    return recipientItem.recipient;
  }

  return null;
};

const preloadCustomer = async () => {
  if (!customerId.value) {
    return;
  }

  if (Number(customer.value?.id) === Number(customerId.value)) {
    return;
  }

  try {
    const response = await apiClient.get(`/api/customer/${customerId.value}`);
    const loaded = response.data ?? null;
    if (loaded?.id) {
      await setCustomer(loaded);
    }
  } catch (err) {
    // Non-blocking: quote can still be created manually.
  }
};

watch(
  customerId,
  async (nextCustomerId) => {
    if (!nextCustomerId) {
      customer.value = null;
      return;
    }

    await preloadCustomer();
  },
  { immediate: true },
);

const preloadTicketRecipient = async () => {
  if (!ticketId.value) return;
  if (recipientContact.value?.id) return;

  try {
    const response = await apiClient.get(`/api/ticket/${ticketId.value}`);
    const ticket = response.data ?? null;
    const contact = resolveTicketContact(ticket);
    if (contact?.id) {
      recipientContact.value = contact;
    }
    if (!description.value?.trim()) {
      const inferredName = resolveTicketName(ticket);
      if (inferredName) {
        description.value = inferredName;
      }
    }
  } catch (err) {
    // Non-blocking: quote can still be created manually.
  }
};

const stopCreateProgressTimer = () => {
  if (createProgressTimer) {
    clearInterval(createProgressTimer);
    createProgressTimer = null;
  }
};

const startCreateProgress = () => {
  createProgress.value = 8;
  stopCreateProgressTimer();
  createProgressTimer = setInterval(() => {
    if (createProgress.value >= 92) return;
    createProgress.value = Math.min(
      92,
      createProgress.value +
        (createProgress.value < 55
          ? createProgressFastStep
          : createProgressSlowStep),
    );
  }, createProgressIntervalMs);
};

const completeCreateProgress = () => {
  stopCreateProgressTimer();
  createProgress.value = 100;
};

const stopExtractProgressTimer = () => {
  if (extractProgressTimer) {
    clearInterval(extractProgressTimer);
    extractProgressTimer = null;
  }
};

const clearExtractPollTimer = () => {
  if (extractPollTimer) {
    clearTimeout(extractPollTimer);
    extractPollTimer = null;
  }
};

const stopExtractPolling = () => {
  clearExtractPollTimer();
  activeExtractionId.value = null;
};

const startExtractProgress = () => {
  extractProgress.value = 8;
  stopExtractProgressTimer();
  extractProgressTimer = setInterval(() => {
    if (extractProgress.value >= 92) return;
    extractProgress.value = Math.min(
      92,
      extractProgress.value +
        (extractProgress.value < 58
          ? extractProgressFastStep
          : extractProgressSlowStep),
    );
  }, extractProgressIntervalMs);
};

const completeExtractProgress = () => {
  stopExtractProgressTimer();
  extractProgress.value = 100;
};

const resetExtractProgress = () => {
  stopExtractProgressTimer();
  extractProgress.value = 0;
  extractStage.value = "";
};

const resetCreateProgress = () => {
  stopCreateProgressTimer();
  createProgress.value = 0;
  createStage.value = "";
};

const clearExtractedDraft = ({ restore = true } = {}) => {
  extractionGeneration += 1;
  stopExtractPolling();
  extractLoading.value = false;
  extractError.value = null;
  resetExtractProgress();
  extractedDraft.value = null;
  extractedQuestions.value = [];
  extractedMatchedCustomer.value = null;
  extractedMatchedRecipient.value = null;
  suggestedCustomer.value = null;
  suggestedRecipient.value = null;
  if (restore && extractedDraftApplied.value && preExtractionValues.value) {
    customer.value = preExtractionValues.value.customer;
    recipientContact.value = preExtractionValues.value.recipientContact;
    description.value = preExtractionValues.value.description;
    notes.value = preExtractionValues.value.notes;
  }
  extractedDraftApplied.value = false;
  extractionQuestionsAcknowledged.value = false;
  preExtractionValues.value = null;
};

const applyExtractedPayload = (payload) => {
  extractedDraft.value = clonePayload(payload?.draft ?? null);
  extractedQuestions.value = Array.isArray(payload?.questions)
    ? payload.questions
    : [];
  extractedMatchedCustomer.value = payload?.matched_customer ?? null;
  extractedMatchedRecipient.value = payload?.matched_recipient_contact ?? null;
  suggestedCustomer.value = payload?.suggested_customer ?? null;
  suggestedRecipient.value = payload?.suggested_recipient ?? null;

};

const applyExtractedSelection = () => {
  if (!extractedDraft.value) return;
  if (!extractedDraftApplied.value) {
    preExtractionValues.value = {
      customer: clonePayload(customer.value),
      recipientContact: clonePayload(recipientContact.value),
      description: description.value,
      notes: notes.value,
    };
  }
  if (extractedMatchedCustomer.value?.id) {
    customer.value = extractedMatchedCustomer.value;
  }
  if (extractedMatchedRecipient.value?.id) {
    recipientContact.value = extractedMatchedRecipient.value;
  }
  if (extractedDraft.value?.description) {
    description.value = extractedDraft.value.description;
  }
  if (extractedDraft.value?.notes && !notes.value.trim()) {
    notes.value = extractedDraft.value.notes;
  }
  extractedDraftApplied.value = true;
  error.value = null;
};

const queueExtractPoll = () => {
  clearExtractPollTimer();
  if (!activeExtractionId.value) {
    return;
  }

  extractPollTimer = window.setTimeout(() => {
    pollExtractStatus();
  }, extractPollIntervalMs);
};

const pollExtractStatus = async () => {
  if (!activeExtractionId.value) {
    return false;
  }

  const currentGeneration = extractionGeneration;
  try {
    const response = await apiClient.get(
      `/api/quote/extract-draft/${activeExtractionId.value}`,
      {
        suppressAutoReload: true,
        suppressErrorAlert: true,
      },
    );
    if (currentGeneration !== extractionGeneration) return false;
    if (!response) {
      throw new Error("AI extraction status request did not complete.");
    }

    const payload = unwrapApiData(response) ?? {};
    const status = String(payload?.status ?? "").toLowerCase();

    if (status === "completed") {
      applyExtractedPayload(payload);
      extractStage.value = "Draft extracted";
      completeExtractProgress();
      stopExtractPolling();
      extractLoading.value = false;
      return true;
    }

    if (status === "failed") {
      const message =
        payload?.error ?? "Unable to extract quote details from this email.";
      clearExtractedDraft();
      extractError.value = message;
      extractLoading.value = false;
      return false;
    }

    extractStage.value =
      status === "processing"
        ? "Reading attachments and extracting quote draft…"
        : "Queued for AI extraction…";
    queueExtractPoll();
    return false;
  } catch (err) {
    if (currentGeneration !== extractionGeneration) return false;
    console.error(err);
    clearExtractedDraft();
    extractError.value =
      err.response?.data?.message ?? "Unable to extract quote details from this email.";
    extractLoading.value = false;
    return false;
  }
};

const onManualAttachmentChange = (event) => {
  manualEmailAttachments.value = Array.from(event.target.files ?? []);
  attachmentUploadError.value = null;
};

const removeManualAttachment = (index) => {
  manualEmailAttachments.value = manualEmailAttachments.value.filter(
    (_, fileIndex) => fileIndex !== index,
  );
};

const isEventType = (type) => {
  const shortname = String(type?.shortname ?? "")
    .trim()
    .toLowerCase();
  const name = String(type?.name ?? "")
    .trim()
    .toLowerCase();
  return shortname.includes("event") || name.includes("event");
};

const isPtsType = (type) => {
  const shortname = String(type?.shortname ?? "").trim().toLowerCase();
  const name = String(type?.name ?? "").trim().toLowerCase();
  return shortname === "pts" || ["patient transportation", "patient transport", "pts"].includes(name);
};

const clonePayload = (value) => JSON.parse(JSON.stringify(value ?? null));

const unwrapApiData = (response) => response?.data?.data ?? response?.data ?? null;

const extractManualEmail = async () => {
  if (!selectedType.value) {
    extractError.value = "Select a supported quote type before running AI extraction.";
    return false;
  }

  if (!manualExtractionEnabled.value) {
    extractError.value = "AI extraction is currently available for event and PTS quotes.";
    return false;
  }

  if (!manualEmailText.value.trim()) {
    extractError.value = "Paste the enquiry email before running AI extraction.";
    return false;
  }

  clearExtractedDraft();
  const currentGeneration = extractionGeneration;
  extractLoading.value = true;
  extractStage.value = manualEmailAttachments.value.length
    ? "Reading attachments and running AI extraction…"
    : "Running AI extraction…";
  startExtractProgress();

  try {
    const formData = new FormData();
    formData.append("reference_type_id", selectedType.value);
    if (manualEmailSubject.value) {
      formData.append("email_subject", manualEmailSubject.value);
    }
    formData.append("email_text", manualEmailText.value);
    manualEmailAttachments.value.forEach((file, index) => {
      formData.append(`attachments[${index}]`, file);
    });

    const response = await apiClient.post(
      "/api/quote/extract-draft",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        suppressAutoReload: true,
        suppressErrorAlert: true,
      },
    );
    if (currentGeneration !== extractionGeneration) return false;
    if (!response) {
      throw new Error("AI extraction request did not complete.");
    }
    const payload = unwrapApiData(response) ?? {};
    const extractionId = Number(payload?.id ?? 0);
    if (!extractionId) {
      throw new Error("AI extraction request did not return an extraction ID.");
    }

    activeExtractionId.value = extractionId;
    extractStage.value =
      String(payload?.status ?? "").toLowerCase() === "processing"
        ? "Reading attachments and extracting quote draft…"
        : "Queued for AI extraction…";
    queueExtractPoll();
    return true;
  } catch (err) {
    if (currentGeneration !== extractionGeneration) return false;
    console.error(err);
    clearExtractedDraft();
    extractError.value =
      err.response?.data?.message ?? "Unable to extract quote details from this email.";
    extractLoading.value = false;
    return false;
  }
};

const applyExtractedDraftToQuote = async (quotePayload) => {
  if (
    !manualExtractionEnabled.value ||
    !extractedDraftApplied.value ||
    !extractedDraft.value?.versions?.length
  ) {
    return quotePayload;
  }

  const quoteId = Number(quotePayload?.id ?? 0);
  if (!quoteId) {
    throw new Error("Quote ID missing before applying extracted draft");
  }

  const firstVersionId = quotePayload?.versions?.[0]?.id ?? null;
  const versions = clonePayload(extractedDraft.value.versions) ?? [];
  if (versions[0] && firstVersionId) {
    versions[0].id = firstVersionId;
  }

  createStage.value = "Applying extracted email draft…";

  const response = await apiClient.post(`/api/quote/${quoteId}/manage`, {
    customer_id: customer.value?.id ?? null,
    recipient_contact_id: recipientContact.value?.id ?? null,
    reference_type_id: selectedType.value,
    description: description.value || extractedDraft.value?.description || null,
    notes: notes.value || extractedDraft.value?.notes || null,
    status: quotePayload?.status ?? "draft",
    refresh_pricing: false,
    versions,
  });

  return unwrapApiData(response) ?? quotePayload;
};

const uploadQuoteAttachments = async (quoteId) => {
  if (!manualEmailAttachments.value.length) {
    return true;
  }

  attachmentUploadError.value = null;
  createStage.value = "Uploading quote files…";

  const formData = new FormData();
  manualEmailAttachments.value.forEach((file, index) => {
    formData.append(`files[${index}]`, file);
  });

  try {
    const response = await apiClient.post(`/api/quote/${quoteId}/files`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (!response) {
      throw new Error("Quote file upload did not complete.");
    }
    return true;
  } catch (err) {
    console.error(err);
    attachmentUploadError.value =
      err.response?.data?.message ?? "Quote created, but file upload failed.";
    return false;
  }
};

const continueToCreatedQuote = () => {
  if (!createdQuoteId.value) return;
  const query = ticketId.value ? { ticket_id: ticketId.value } : {};
  router.push({ path: `/quotes/${createdQuoteId.value}`, query });
};

const finishCreatedQuote = async () => {
  let quotePayload = createdQuotePayload.value;
  quotePayload = await applyExtractedDraftToQuote(quotePayload);
  createdQuotePayload.value = quotePayload;

  if (manualEmailAttachments.value.length) {
    const uploaded = await uploadQuoteAttachments(createdQuoteId.value);
    if (!uploaded) {
      completeCreateProgress();
      return false;
    }
  }

  completeCreateProgress();
  continueToCreatedQuote();
  return true;
};

const createQuote = async () => {
  if (!selectedType.value) {
    error.value = "Please select a quote type.";
    return false;
  }

  if (extractLoading.value) {
    error.value = "Wait for AI extraction to finish before creating the quote.";
    return false;
  }

  if (!description.value.trim()) {
    error.value = "Add a short quote name before creating the quote.";
    return false;
  }

  if (
    extractedDraftApplied.value &&
    extractedQuestions.value.length &&
    !extractionQuestionsAcknowledged.value
  ) {
    error.value = "Confirm that the extraction questions have been reviewed.";
    return false;
  }

  loading.value = true;
  error.value = null;
  attachmentUploadError.value = null;
  startCreateProgress();

  createStage.value = "Creating quote…";

  try {
    if (createdQuoteId.value) {
      createStage.value = "Finishing existing quote…";
      return await finishCreatedQuote();
    }

    const payload = {
      reference_type_id: selectedType.value,
      customer_id: customer.value?.id ?? null,
      recipient_contact_id: recipientContact.value?.id ?? null,
      description: description.value || null,
      notes: notes.value || null,
    };
    const response = ticketId.value
      ? await apiClient.post(`/api/ticket/${ticketId.value}/quote`, payload)
      : await apiClient.post("/api/quote", payload);
    const quotePayload = unwrapApiData(response);
    const quoteId = quotePayload?.id;
    if (!quoteId) {
      throw new Error("Quote ID missing from response");
    }

    createdQuoteId.value = quoteId;
    createdQuotePayload.value = quotePayload;
    return await finishCreatedQuote();
  } catch (err) {
    console.error(err);
    error.value = createdQuoteId.value
      ? err.response?.data?.message ??
        `Quote #${createdQuoteId.value} was created, but setup did not finish. Retry setup or continue to the quote.`
      : err.response?.data?.message ?? "Unable to create quote.";
    resetCreateProgress();
    return false;
  } finally {
    loading.value = false;
  }
};

const setCustomer = async (value) => {
  customer.value = value ?? null;
  if (!value?.id) return;

  try {
    const response = await apiClient.get(`/api/customer/${value.id}`);
    const fullCustomer = response.data?.data ?? response.data ?? value;
    customer.value = fullCustomer;
    const contacts = Array.isArray(fullCustomer.contacts)
      ? fullCustomer.contacts
      : [];
    const currentRecipientId = Number(recipientContact.value?.id);
    if (
      currentRecipientId > 0 &&
      !contacts.some((contact) => Number(contact.id) === currentRecipientId)
    ) {
      recipientContact.value = null;
    }
    if (!recipientContact.value?.id) {
      recipientContact.value =
        fullCustomer.contact ??
        contacts.find(
          (contact) => Number(contact.id) === Number(fullCustomer.contact_id),
        ) ??
        null;
    }
  } catch (err) {
    error.value =
      "Customer selected, but their contacts could not be checked. Choose the recipient before continuing.";
  }
};

const setRecipientContact = (value) => {
  recipientContact.value = value ?? null;
};

watch(
  manualExtractionEnabled,
  (enabled) => {
    if (!enabled) {
      clearExtractedDraft();
    }
  },
  { immediate: true },
);

watch([manualEmailSubject, manualEmailText, manualEmailAttachments], () => {
  clearExtractedDraft();
});

watch(selectedType, () => {
  extractError.value = null;
  attachmentUploadError.value = null;
  clearExtractedDraft();
});
</script>

<template>
  <div class="space-y-6">
    <div class="box">
      <h1 class="text-2xl font-semibold">Create Quote</h1>
      <p class="text-sm text-slate-500">
        Start a new quote and capture risk assessment details.
      </p>
      <p v-if="ticketId" class="text-sm text-slate-500 mt-2">
        This quote will be linked to the selected ticket and issued a reference
        as soon as it is created.
      </p>
    </div>

    <div class="box space-y-4">
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label for="quote-type" class="block text-xs font-medium text-slate-500">Quote Type</label>
          <select id="quote-type" class="w-full rounded border p-2" v-model="selectedType">
            <option :value="null">Select quote type</option>
            <option v-for="type in types" :key="type.id" :value="type.id">
              {{ type.name }}
            </option>
          </select>
        </div>
        <div>
          <span class="block text-xs font-medium text-slate-500">Starting Point</span>
          <div class="mt-1 grid grid-cols-2 gap-2">
            <button
              type="button"
              class="btn justify-center"
              :class="creationMode === 'email' ? 'accent' : ''"
              @click.prevent="creationMode = 'email'"
            >
              Extract email
            </button>
            <button
              type="button"
              class="btn justify-center"
              :class="creationMode === 'manual' ? 'accent' : ''"
              @click.prevent="creationMode = 'manual'"
            >
              Start blank
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="creationMode === 'email'" class="box space-y-4">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h2 class="text-lg font-semibold">Create From Email</h2>
          <p class="text-sm text-slate-500">
            Paste an enquiry email and Neptune will extract the quote details
            into a draft quote before you create it.
          </p>
        </div>
        <button
          v-if="extractedDraft"
          class="btn"
          @click.prevent="clearExtractedDraft"
        >
          Clear Draft
        </button>
      </div>

      <div
        v-if="selectedType && !manualExtractionEnabled"
        class="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
      >
        AI extraction is currently available for event and PTS quotes only.
      </div>

      <template v-else>
        <div>
          <label class="block text-xs text-slate-500">Email Subject</label>
          <input
            v-model="manualEmailSubject"
            class="w-full p-2 rounded border"
            type="text"
            placeholder="Optional subject line"
            :disabled="extractLoading"
          />
        </div>

        <div>
          <label class="block text-xs text-slate-500">Email Content</label>
          <textarea
            v-model="manualEmailText"
            class="w-full p-2 rounded border"
            rows="10"
            placeholder="Paste the enquiry email here, including any forwarded headers if you have them."
            :disabled="extractLoading"
          ></textarea>
          <p class="text-xs text-slate-500 mt-1">
            Include sender details and any dates, addresses, patient or event
            requirements present in the email.
          </p>
        </div>

        <div>
          <label class="block text-xs text-slate-500">Attachments</label>
          <label class="btn cursor-pointer mt-1 inline-flex">
            <input
              class="hidden"
              type="file"
              multiple
              :disabled="extractLoading"
              @change="onManualAttachmentChange"
            />
            Select Files
          </label>
          <p class="text-xs text-slate-500 mt-1">
            Upload supporting PDFs, spreadsheets, documents, or images that
            should be scanned into the quote draft.
          </p>
          <div
            v-if="manualEmailAttachments.length"
            class="mt-3 space-y-2 rounded border border-slate-200 p-3"
          >
            <div
              v-for="(file, index) in manualEmailAttachments"
              :key="`${file.name}-${index}`"
              class="flex items-center justify-between gap-3 text-sm"
            >
              <div class="min-w-0">
                <div class="truncate font-medium text-slate-900">
                  {{ file.name }}
                </div>
                <div class="text-xs text-slate-500">
                  {{ Math.max(1, Math.round((file.size ?? 0) / 1024)) }} KB
                </div>
              </div>
              <button
                class="btn"
                type="button"
                :disabled="extractLoading"
                @click.prevent="removeManualAttachment(index)"
              >
                Remove
              </button>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-3">
        <button
          class="btn accent"
          @click.prevent="extractManualEmail"
          :disabled="extractLoading"
        >
            {{ extractLoading ? "Extracting…" : "Extract Email Into Draft" }}
        </button>
          <span v-if="extractError" class="text-sm text-rose-500">
            {{ extractError }}
          </span>
        </div>

        <div v-if="extractLoading || extractProgress > 0" class="max-w-md">
          <div class="flex items-center justify-between text-xs text-slate-500 mb-1">
            <span>{{ extractStage || "Extracting quote draft…" }}</span>
            <span>{{ extractProgress }}%</span>
          </div>
          <div class="h-2 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              class="h-full rounded-full bg-emerald-500 transition-all duration-300 ease-out"
              :style="{ width: `${extractProgress}%` }"
            ></div>
          </div>
        </div>

        <div
          v-if="!extractLoading && extractedDraft && extractProgress === 100"
          class="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
        >
          Draft extracted. Review the suggestions, then apply them explicitly.
        </div>

        <div
          v-if="extractedDraft"
          class="rounded border border-emerald-200 bg-emerald-50/60 p-4 space-y-4"
        >
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div class="text-xs uppercase tracking-wide text-slate-500">
                {{ isPtsQuote ? "Patient Transport" : "Event" }}
              </div>
              <div class="font-medium text-slate-900">
                {{ extractedDraft.description || "Not identified" }}
              </div>
            </div>
            <div>
              <div class="text-xs uppercase tracking-wide text-slate-500">
                Dates
              </div>
              <div class="font-medium text-slate-900">
                {{ extractedDateCount || 0 }}
              </div>
            </div>
            <div>
              <div class="text-xs uppercase tracking-wide text-slate-500">
                Suggested Resources
              </div>
              <div class="font-medium text-slate-900">
                {{ extractedResourceCount || 0 }}
              </div>
            </div>
            <div>
              <div class="text-xs uppercase tracking-wide text-slate-500">
                Attachments
              </div>
              <div class="font-medium text-slate-900">
                {{ extractedAttachmentCount || 0 }}
              </div>
            </div>
          </div>

          <div v-if="!isPtsQuote" class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div class="text-xs uppercase tracking-wide text-slate-500">
                Address
              </div>
              <div class="text-slate-900">
                {{ extractedEventRisk.address || "Not identified" }}
              </div>
            </div>
            <div>
              <div class="text-xs uppercase tracking-wide text-slate-500">
                Attendance
              </div>
              <div class="text-slate-900">
                {{ extractedEventRisk.max_people ?? extractedEventRisk.expected_peak_attendance ?? "Not identified" }}
              </div>
            </div>
          </div>

          <div v-if="isPtsQuote" class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div v-for="(label, field) in { patient_name: 'Patient', pickup_address: 'Pickup Address', dropoff_address: 'Drop-off Address', pickup_time: 'Pickup Time', mobility: 'Mobility', equipment: 'Equipment' }" :key="field">
              <div class="text-xs uppercase tracking-wide text-slate-500">{{ label }}</div>
              <div class="text-slate-900">{{ extractedPtsRisk[field] || "Not identified" }}</div>
            </div>
            <div>
              <div class="text-xs uppercase tracking-wide text-slate-500">Return Journey</div>
              <div class="text-slate-900">{{ extractedPtsRisk.return_required == null ? "Not identified" : extractedPtsRisk.return_required ? "Required" : "Not required" }}</div>
            </div>
          </div>

          <div
            v-if="extractedMatchedCustomer || suggestedCustomer"
            class="text-sm text-slate-700"
          >
            <span class="font-medium text-slate-900">Customer:</span>
            {{
              extractedMatchedCustomer?.company
                || suggestedCustomer?.company
                || "No customer match"
            }}
            <span v-if="extractedMatchedCustomer" class="text-emerald-700">
              matched to an existing customer
            </span>
          </div>

          <div
            v-if="extractedMatchedRecipient || suggestedRecipient"
            class="text-sm text-slate-700"
          >
            <span class="font-medium text-slate-900">Recipient:</span>
            {{
              extractedMatchedRecipient?.name
                || suggestedRecipient?.name
                || recipientContactLabel
                || "No recipient identified"
            }}
            <span class="text-slate-500">
              {{
                extractedMatchedRecipient?.email
                  || suggestedRecipient?.email
                  || ""
              }}
            </span>
          </div>

          <div
            v-if="extractedQuestions.length"
            class="rounded border border-amber-200 bg-white px-3 py-2 text-sm text-amber-900"
          >
            Missing information:
            {{ extractedQuestions.join(" • ") }}
          </div>

          <label
            v-if="extractedQuestions.length && extractedDraftApplied"
            class="flex items-start gap-2 rounded border border-amber-200 bg-white p-3 text-sm text-amber-900"
          >
            <input v-model="extractionQuestionsAcknowledged" type="checkbox" class="mt-1" />
            <span>I have reviewed these questions and will complete the missing details in the wizard.</span>
          </label>

          <div class="flex flex-wrap items-center gap-3">
            <button
              class="btn accent"
              type="button"
              :disabled="extractedDraftApplied"
              @click.prevent="applyExtractedSelection"
            >
              {{ extractedDraftApplied ? "Suggestions applied" : "Apply suggestions" }}
            </button>
            <span v-if="!extractedDraftApplied" class="text-xs text-slate-500">
              Nothing will overwrite the form until you apply it.
            </span>
          </div>

          <p class="text-xs text-slate-500">
            Applied suggestions can still be edited before the quote is created.
            Clearing the draft restores the values that were here beforehand.
          </p>
        </div>
      </template>
    </div>

    <div class="box space-y-4">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-xs text-slate-500">Customer</label>
          <CustomerSearch
            :customer="customer"
            :resetAfterSelection="false"
            @selected="setCustomer"
          />
        </div>
        <div>
          <label class="block text-xs text-slate-500">Recipient Contact</label>
          <ContactSearch
            placeholder="Find or add recipient..."
            :value="recipientContactLabel"
            :autoShowResults="false"
            :customerId="customer?.id"
            @selected="setRecipientContact"
          />
        </div>
      </div>

      <div>
        <label class="block text-xs text-slate-500"
          >Event / Quote Short Name</label
        >
        <textarea
          class="w-full p-2 rounded border"
          rows="2"
          v-model="description"
          placeholder="Short title used to identify this quote (e.g. River Run Festival)"
        ></textarea>
        <p class="text-xs text-slate-500 mt-1">
          Use a short name, not a full description.
        </p>
      </div>
      <div>
        <label class="block text-xs text-slate-500">Notes</label>
        <textarea
          class="w-full p-2 rounded border"
          rows="2"
          v-model="notes"
        ></textarea>
      </div>

      <div class="flex items-center gap-3">
        <button
          class="btn accent"
          @click.prevent="createQuote"
          :disabled="loading || extractLoading"
        >
          {{ createdQuoteId ? "Retry setup" : "Create Quote" }}
        </button>
        <span v-if="loading" class="text-sm text-slate-500">
          {{ createStage || "Creating…" }}
        </span>
        <span v-else-if="extractLoading" class="text-sm text-slate-500">
          AI extraction is still running.
        </span>
        <span v-if="error" class="text-sm text-rose-500">{{ error }}</span>
      </div>
      <div v-if="attachmentUploadError" class="text-sm text-amber-700">
        {{ attachmentUploadError }}
        <div class="mt-2 flex flex-wrap gap-2">
          <button class="btn accent" type="button" :disabled="loading" @click.prevent="createQuote">
            Retry file upload
          </button>
          <button class="btn" type="button" :disabled="loading" @click.prevent="continueToCreatedQuote">
            Continue without files
          </button>
        </div>
      </div>
      <div v-else-if="createdQuoteId && error" class="flex flex-wrap gap-2">
        <button class="btn accent" type="button" :disabled="loading" @click.prevent="createQuote">
          Retry setup
        </button>
        <button class="btn" type="button" :disabled="loading" @click.prevent="continueToCreatedQuote">
          Open quote #{{ createdQuoteId }}
        </button>
      </div>

      <div v-if="loading" class="max-w-md">
        <div class="flex items-center justify-between text-xs text-slate-500 mb-1">
          <span>{{ createStage || "Creating quote…" }}</span>
          <span>{{ createProgress }}%</span>
        </div>
        <div class="h-2 w-full overflow-hidden rounded-full bg-slate-200">
          <div
            class="h-full rounded-full bg-amber-500 transition-all duration-300 ease-out"
            :style="{ width: `${createProgress}%` }"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>
